import { useCallback } from 'react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { ArrowLeftStartOnRectangleIcon, UserCircleIcon } from '@heroicons/react/24/outline'

import { Avatar } from '@authy/lib-client/components'
import { env } from '@authy/lib-client/env'
import { Menu, Dropdown } from '@authy/lib-web/components'

import { adminAppState } from '@/appState'
import { itemHoverActiveClasName, itemActiveClassName, itemInactiveClassName } from '@/App/Navigation/Sidebar/utils'

type AppTenantProps = {
    collapsed: boolean
    onActivate: () => void
}

export const UserMenu = observer(({ collapsed, onActivate }: AppTenantProps) => {
    const { t } = useTranslation()

    const { user } = adminAppState

    const handleLogout = useCallback(() => {
        onActivate()
        adminAppState.auth.logout()
    }, [onActivate])

    if (!user) {
        return null
    }

    const profileLink: any = {}
    if (adminAppState.applicationKey === 'root') {
        profileLink.to = '/user/profile'
    } else {
        const appUrl = env.VITE_PRODUCT_URL_ROOT
        profileLink.href = `${appUrl}/user/profile`
    }

    return (
        <Dropdown
            dropdownArrow={collapsed ? 'none' : 'hover'}
            className={classNames(
                'flex w-full items-center gap-x-3 rounded-md text-left',
                itemHoverActiveClasName,
                itemInactiveClassName,
                {
                    'h-12 px-2': !collapsed,
                    'justify-center p-1': collapsed,
                }
            )}
            activeClassName={itemActiveClassName}
            menuItems={
                <>
                    <Menu.Item Icon={UserCircleIcon} {...profileLink} onClick={onActivate}>
                        {t('app.user.myProfile')}
                    </Menu.Item>
                    <Menu.ItemDivider />
                    <Menu.Item Icon={ArrowLeftStartOnRectangleIcon} onClick={handleLogout}>
                        {t('app.user.logout')}
                    </Menu.Item>
                </>
            }
        >
            <Avatar
                className={classNames('shrink-0 grow-0 rounded-full', {
                    'size-8': !collapsed,
                    'size-7': collapsed,
                })}
                variant="bot"
                src={user.avatar?.resource}
                name={user.name}
            />
            {!collapsed && <div className="flex-1 font-medium">{user.name}</div>}
        </Dropdown>
    )
})
